import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

interface BurnUpData {
  chartData: Array<{
    date: string;
    cumulativeFixed: number;
  }>;
}

interface DashboardMetrics {
  criticalIssues: number;
  issuesByType?: Record<string, number>;
  totalFlows?: number;
}

interface BurnDownChartProps {
  burnUpData: BurnUpData | null;
  metrics: DashboardMetrics | null;
  selectedOrgId: string | null;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export function BurnDownChart({ burnUpData, metrics, selectedOrgId }: BurnDownChartProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Show mock data when IN demo mode
  const searchParams = new URLSearchParams(location.search);
  const isDemoMode = searchParams.get('mode') === 'demo';
  const useMockData = isDemoMode;
  
  // Convert burn-up data to burn-down format
  let burnDownData: any[] = [];
  
  if (useMockData) {
    // Mock data showing 70% reduction from July 21 to Aug 3 
    burnDownData = [
      { date: '2025-07-21', totalIssues: 272, fixedIssues: 0, newIssues: 0, flowsModified: [] },
      { date: '2025-07-22', totalIssues: 251, fixedIssues: 21, newIssues: 0, flowsModified: ['Account Creation Flow', 'Lead Conversion'] },
      { date: '2025-07-23', totalIssues: 234, fixedIssues: 17, newIssues: 0, flowsModified: ['Opportunity Workflow'] },
      { date: '2025-07-24', totalIssues: 210, fixedIssues: 24, newIssues: 0, flowsModified: ['Case Escalation', 'Email Alert Flow'] },
      { date: '2025-07-25', totalIssues: 195, fixedIssues: 15, newIssues: 0, flowsModified: ['Contract Approval'] },
      { date: '2025-07-26', totalIssues: 178, fixedIssues: 17, newIssues: 0, flowsModified: ['Quote Generation'] },
      { date: '2025-07-27', totalIssues: 165, fixedIssues: 13, newIssues: 0, flowsModified: [] },
      { date: '2025-07-28', totalIssues: 152, fixedIssues: 13, newIssues: 0, flowsModified: ['Task Assignment'] },
      { date: '2025-07-29', totalIssues: 138, fixedIssues: 14, newIssues: 0, flowsModified: ['Renewal Process', 'Support Ticket Flow'] },
      { date: '2025-07-30', totalIssues: 121, fixedIssues: 17, newIssues: 0, flowsModified: ['Data Quality Check'] },
      { date: '2025-07-31', totalIssues: 108, fixedIssues: 13, newIssues: 0, flowsModified: ['Territory Assignment'] },
      { date: '2025-08-01', totalIssues: 96, fixedIssues: 12, newIssues: 0, flowsModified: ['Commission Calculation'] },
      { date: '2025-08-02', totalIssues: 88, fixedIssues: 8, newIssues: 0, flowsModified: ['Partner Portal Access'] },
      { date: '2025-08-03', totalIssues: 82, fixedIssues: 6, newIssues: 0, flowsModified: ['Campaign Member Status'] }
    ];
  } else if (burnUpData && burnUpData.chartData && burnUpData.chartData.length > 0) {
    // Use actual total issues from the data
    burnDownData = burnUpData.chartData.map((item, index) => {
      // Calculate daily changes
      const prevItem = index > 0 ? burnUpData.chartData[index - 1] : null;
      const fixedIssues = index > 0 ? 
        item.cumulativeFixed - (prevItem?.cumulativeFixed || 0) : 0;
      
      // Calculate new issues (if total went up from previous day)
      // Don't count the first day's issues as "new"
      const prevTotal = prevItem?.totalIssues || 0;
      const currentTotal = item.totalIssues || 0;
      const newIssues = (index > 0 && currentTotal > prevTotal) ? currentTotal - prevTotal : 0;
      
      return {
        date: item.date,
        totalIssues: currentTotal,
        totalFlows: item.totalFlows || metrics?.totalFlows || 176,
        fixedIssues: Math.max(0, fixedIssues),
        newIssues: Math.max(0, newIssues),
        flowsModified: [] // To be populated with flow data if available
      };
    });
    
    // Show last 30 days of data
    if (burnDownData.length > 30) {
      burnDownData = burnDownData.slice(-30);
    }
  }

  const startIssues = burnDownData[0]?.totalIssues || 0;
  const currentIssues = burnDownData[burnDownData.length - 1]?.totalIssues || 0;
  const totalFixed = burnDownData.reduce((sum, d) => sum + d.fixedIssues, 0);
  const totalNew = burnDownData.reduce((sum, d) => sum + d.newIssues, 0);
  
  // Get total flows for dynamic scaling
  const totalFlows = metrics?.totalFlows || 176;
  const yellowThreshold = totalFlows * 0.4; // 40% of total flows
  
  // Add color information to each data point based on requirements
  const dataWithColors = burnDownData.map((point) => {
    const flowCount = point.totalFlows || totalFlows;
    let color = '#10B981'; // green (default for < 40% of flows)
    
    if (point.totalIssues > flowCount) {
      color = '#EF4444'; // red (issues > number of flows)
    } else if (point.totalIssues >= flowCount * 0.4) {
      color = '#F59E0B'; // yellow (issues >= 40% of flows)
    }
    // else stays green (issues < 40% of flows)
    
    return { ...point, color };
  });

  // Determine the overall line color based on current (most recent) values
  let lineColor = '#10B981'; // default green
  if (burnDownData.length > 0) {
    const lastPoint = burnDownData[burnDownData.length - 1];
    const currentIssueCount = lastPoint.totalIssues;
    const currentFlowCount = lastPoint.totalFlows || totalFlows;
    
    if (currentIssueCount > currentFlowCount) {
      lineColor = '#EF4444'; // red (issues > flows)
    } else if (currentIssueCount >= currentFlowCount * 0.4) {
      lineColor = '#F59E0B'; // yellow (issues >= 40% of flows)
    }
    // else stays green
  }

  // Show empty state if no data
  if (!dataWithColors || dataWithColors.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Issues Over Time</h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          <p>No issue tracking data available yet</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Issues Over Time</h3>
      <div style={{ width: '100%', height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={dataWithColors}
            onClick={(e) => {
              if (e && e.activeLabel) {
                navigate(`/${selectedOrgId}/flows?date=${encodeURIComponent(e.activeLabel)}&context=burndown`);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 11 }}
              interval={2}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white p-3 border border-gray-200 rounded shadow-sm">
                      <p className="font-medium text-sm">{formatDate(label)}</p>
                      <p className="text-sm text-gray-600">Total Issues: {data.totalIssues}</p>
                      {data.newIssues > 0 && (
                        <p className="text-sm text-red-600">New Issues: +{data.newIssues}</p>
                      )}
                      {data.fixedIssues > 0 && (
                        <p className="text-sm text-green-600">Fixed: -{data.fixedIssues}</p>
                      )}
                      {data.flowsModified && data.flowsModified.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs font-medium text-gray-700">Flows Modified:</p>
                          <ul className="text-xs text-gray-600 mt-1">
                            {data.flowsModified.slice(0, 3).map((flow: string, idx: number) => (
                              <li key={idx}>• {flow}</li>
                            ))}
                            {data.flowsModified.length > 3 && (
                              <li>• ...and {data.flowsModified.length - 3} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            {/* Render a single line with colored segments */}
            <Line
              type="monotone"
              dataKey="totalIssues"
              stroke={lineColor}
              strokeWidth={2}
              connectNulls={true}
              dot={(props) => {
                const { cx, cy, payload, index } = props;
                if (cx === null || cy === null || cx === undefined || cy === undefined) {
                  return null;
                }
                
                // Get the color for this specific point
                const pointData = dataWithColors[index];
                const color = pointData?.color || '#EF4444';
                
                // Highlight spike points (when issues increase)
                const prevIndex = index - 1;
                const isSpike = prevIndex >= 0 && 
                               payload.totalIssues > dataWithColors[prevIndex].totalIssues + 5;
                
                if (isSpike) {
                  return (
                    <circle cx={cx} cy={cy} r={6} fill={color} stroke="#fff" strokeWidth={2} />
                  );
                }
                return <circle cx={cx} cy={cy} r={4} fill={color} />;
              }}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-500">Current Issues</p>
            <p className="text-lg font-semibold text-red-600">
              {currentIssues}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Issues Fixed</p>
            <p className="text-lg font-semibold text-green-600">
              {totalFixed}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">New Issues</p>
            <p className="text-lg font-semibold text-amber-600">
              {totalNew}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}